interface KnoxRuntimeConfig {
  ENABLE_DEBUG_MODE?: boolean;
  ENABLE_SEMANTIC_CONFIG_TAB?: boolean;
  HIDE_LANGUAGE_SELECTOR?: boolean;
  IS_CN?: boolean;
  SUPPORT_EMAIL?: string;
  /** Runtime-overridable Knox API base URL (e.g. https://console.knoxops.app/api or https://console.ontox.cn/api) */
  KNOX_API_URL?: string;
  /** Runtime-overridable Gateway URL for WebSocket connections */
  GATEWAY_URL?: string;
  /** Runtime-overridable preview service base URL */
  PREVIEW_BASE_URL?: string;
  /** Runtime-overridable Elevo API base URL */
  ELEVO_API_BASE?: string;
  /** Runtime-overridable Knox Service URL (used for session/fetching Matrix credentials) */
  KNOX_SERVICE_URL?: string;
}

function getRuntimeConfig(): KnoxRuntimeConfig {
  return (window as any).__KNOX_CONFIG__ ?? {};
}

export function isDebugMode(): boolean {
  const cfg = getRuntimeConfig();
  if (typeof cfg.ENABLE_DEBUG_MODE === 'boolean') {
    return cfg.ENABLE_DEBUG_MODE;
  }
  return import.meta.env.VITE_ENABLE_DEBUG_MODE === 'true';
}

export function isLanguageSelectorHidden(): boolean {
  return getRuntimeConfig().HIDE_LANGUAGE_SELECTOR === true;
}

export function isSemanticConfigTabEnabled(): boolean {
  return getRuntimeConfig().ENABLE_SEMANTIC_CONFIG_TAB === true;
}

/** isCN reports whether the deployment targets the China market (IS_CN env flag). */
export function isCN(): boolean {
  return getRuntimeConfig().IS_CN === true;
}

/** getSupportEmail returns the support contact email.
 *  Priority: __KNOX_CONFIG__.SUPPORT_EMAIL → default by IS_CN.
 *  Overseas default: service@knoxops.app. China default: service@ontox.cn.
 *  This ensures CN deployments never leak the overseas email even if SUPPORT_EMAIL is unset. */
export function getSupportEmail(): string {
  const cfg = getRuntimeConfig();
  if (cfg.SUPPORT_EMAIL && cfg.SUPPORT_EMAIL.trim() !== '') {
    return cfg.SUPPORT_EMAIL;
  }
  return isCN() ? 'service@ontox.cn' : 'service@knoxops.app';
}

/** getCompanyName returns the legal entity name based on IS_CN flag.
 *  Overseas: Portanta USA Company. China: 优维科技(深圳)有限公司. */
export function getCompanyName(): string {
  return isCN() ? '优维科技(深圳)有限公司' : 'Portanta USA Company';
}

/** getKnoxApiUrl returns the Knox API base URL.
 *  Priority: __KNOX_CONFIG__.KNOX_API_URL → VITE_KNOX_API_URL build-arg → '' */
export function getKnoxApiUrl(): string {
  const cfg = getRuntimeConfig();
  return cfg.KNOX_API_URL || import.meta.env.VITE_KNOX_API_URL || '';
}

/** getGatewayUrl returns the Gateway WebSocket URL.
 *  Priority: __KNOX_CONFIG__.GATEWAY_URL → VITE_GATEWAY_URL build-arg → '' */
export function getGatewayUrl(): string {
  const cfg = getRuntimeConfig();
  return cfg.GATEWAY_URL || import.meta.env.VITE_GATEWAY_URL || '';
}

/** getPreviewBaseUrl returns the preview service base URL.
 *  Priority: __KNOX_CONFIG__.PREVIEW_BASE_URL → VITE_PREVIEW_BASE_URL build-arg → localhost:3041 */
export function getPreviewBaseUrl(): string {
  const cfg = getRuntimeConfig();
  return cfg.PREVIEW_BASE_URL || import.meta.env.VITE_PREVIEW_BASE_URL || 'http://localhost:3041';
}

/** getElevoApiBase returns the Elevo API base URL.
 *  Priority: __KNOX_CONFIG__.ELEVO_API_BASE → VITE_ELEVO_API_BASE build-arg → '' */
export function getElevoApiBase(): string {
  const cfg = getRuntimeConfig();
  return cfg.ELEVO_API_BASE || import.meta.env.VITE_ELEVO_API_BASE || '';
}

/** getKnoxServiceUrl returns the Knox Service URL.
 *  Priority: __KNOX_CONFIG__.KNOX_SERVICE_URL → VITE_KNOX_SERVICE_URL build-arg → '' */
export function getKnoxServiceUrl(): string {
  const cfg = getRuntimeConfig();
  return cfg.KNOX_SERVICE_URL || import.meta.env.VITE_KNOX_SERVICE_URL || '';
}
